import React, { useState, useEffect } from 'react';
import API from '../../utils/api';

const Attendance = () => {
  const [activeTab, setActiveTab] = useState('mark');
  const [method, setMethod] = useState('id_number');
  const [identifier, setIdentifier] = useState('');
  const [status, setStatus] = useState('present');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [multipleMembers, setMultipleMembers] = useState([]);
  const [attendanceList, setAttendanceList] = useState([]);
  const [summary, setSummary] = useState([]);
  const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [recentlyMarked, setRecentlyMarked] = useState([]);

  useEffect(() => {
    if (activeTab === 'reports') fetchAttendance();
    if (activeTab === 'summary') fetchSummary();
  }, [activeTab, filterDate]);

  const showMessage = (msg, type) => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(''), 4000);
  };

  const fetchAttendance = async () => {
    try {
      setLoading(true);
      const res = await API.get(`/attendance?date=${filterDate}`);
      setAttendanceList(res.data);
    } catch (error) {
      console.error('Error fetching attendance:', error);
    }
    setLoading(false);
  };

  const fetchSummary = async () => {
    try {
      setLoading(true);
      const res = await API.get('/attendance/summary');
      setSummary(res.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
    setLoading(false);
  };

  const handleMark = async () => {
    if (!identifier.trim()) {
      showMessage('Please enter an identifier!', 'error');
      return;
    }
    try {
      setLoading(true);
      setMultipleMembers([]);
      const res = await API.post('/attendance/mark', {
        identifier: identifier.trim(),
        method,
        status,
        date,
      });

      if (res.data.multiple) {
        setMultipleMembers(res.data.members);
        setLoading(false);
        return;
      }

      showMessage(res.data.message, 'success');
      setRecentlyMarked(prev => [
        { ...res.data.member, status, time: new Date().toLocaleTimeString() },
        ...prev.slice(0, 9)
      ]);
      setIdentifier('');
    } catch (error) {
      showMessage(error.response?.data?.message || 'Something went wrong!', 'error');
    }
    setLoading(false);
  };

  const handleSelectMember = async (member) => {
    try {
      setLoading(true);
      setMultipleMembers([]);
      const res = await API.post('/attendance/mark', {
        identifier: member.school_id,
        method: 'id_number',
        status,
        date,
      });
      showMessage(res.data.message, 'success');
      setRecentlyMarked(prev => [
        { ...member, status, time: new Date().toLocaleTimeString() },
        ...prev.slice(0, 9)
      ]);
      setIdentifier('');
    } catch (error) {
      showMessage(error.response?.data?.message || 'Something went wrong!', 'error');
    }
    setLoading(false);
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>📋 Attendance</h2>
          <p style={styles.subtitle}>Mark and monitor member attendance</p>
        </div>
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        {[
          { id: 'mark', label: '✏️ Mark Attendance' },
          { id: 'reports', label: '📊 Daily Reports' },
          { id: 'summary', label: '📈 Summary' },
        ].map(tab => (
          <button
            key={tab.id}
            style={activeTab === tab.id ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Message */}
      {message && (
        <div style={messageType === 'success' ? styles.successMsg : styles.errorMsg}>
          {message}
        </div>
      )}

      {/* MARK ATTENDANCE TAB */}
      {activeTab === 'mark' && (
        <div style={styles.markContainer}>
          <div style={styles.markCard}>
            <h3 style={styles.cardTitle}>Mark Attendance</h3>

            {/* Date */}
            <div style={styles.field}>
              <label style={styles.label}>Date</label>
              <input
                style={styles.input}
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
              />
            </div>

            {/* Method */}
            <div style={styles.field}>
              <label style={styles.label}>Method</label>
              <div style={styles.methodRow}>
                {[
                  { id: 'id_number', label: '🪪 ID Number' },
                  { id: 'last_name', label: '👤 Last Name' },
                  { id: 'qr_code', label: '📷 QR Code' },
                ].map(m => (
                  <button
                    key={m.id}
                    style={method === m.id ? styles.methodActive : styles.methodBtn}
                    onClick={() => setMethod(m.id)}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Identifier Input */}
            <div style={styles.field}>
              <label style={styles.label}>
                {method === 'id_number' ? 'School ID' :
                  method === 'last_name' ? 'Last Name' : 'QR Code'}
              </label>
              {method === 'qr_code' ? (
                <div style={styles.qrBox}>
                  <p style={styles.qrText}>📷 QR Scanner</p>
                  <p style={styles.qrSub}>Point camera at member QR code</p>
                  <input
                    style={styles.input}
                    type="text"
                    placeholder="Or type school ID manually"
                    value={identifier}
                    onChange={e => setIdentifier(e.target.value)}
                    onKeyPress={e => e.key === 'Enter' && handleMark()}
                  />
                </div>
              ) : (
                <input
                  style={styles.input}
                  type="text"
                  placeholder={method === 'id_number' ?
                    'ex. 211-01312' : 'ex. Angaco'}
                  value={identifier}
                  onChange={e => setIdentifier(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && handleMark()}
                  autoFocus
                />
              )}
            </div>

            {/* Status */}
            <div style={styles.field}>
              <label style={styles.label}>Status</label>
              <div style={styles.statusRow}>
                {[
                  { id: 'present', label: '✅ Present', color: '#2ecc71' },
                  { id: 'absent', label: '❌ Absent', color: '#e74c3c' },
                  { id: 'late', label: '⏰ Late', color: '#f39c12' },
                ].map(s => (
                  <button
                    key={s.id}
                    style={{
                      ...styles.statusBtn,
                      background: status === s.id ? s.color : 'white',
                      color: status === s.id ? 'white' : '#666',
                      border: `2px solid ${s.color}`,
                    }}
                    onClick={() => setStatus(s.id)}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Mark Button */}
            <button
              style={loading ? styles.markBtnDisabled : styles.markBtn}
              onClick={handleMark}
              disabled={loading}
            >
              {loading ? 'Marking...' : '✓ Mark Attendance'}
            </button>

            {/* Multiple Members Found */}
            {multipleMembers.length > 0 && (
              <div style={styles.multipleBox}>
                <p style={styles.multipleTitle}>
                  Multiple members found — select one:
                </p>
                {multipleMembers.map(m => (
                  <button
                    key={m.id}
                    style={styles.memberSelectBtn}
                    onClick={() => handleSelectMember(m)}
                  >
                    <span style={styles.memberSelectName}>
                      {m.first_name} {m.last_name}
                    </span>
                    <span style={styles.memberSelectId}>{m.school_id}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Recently Marked */}
          <div style={styles.recentCard}>
            <h3 style={styles.cardTitle}>Recently Marked</h3>
            {recentlyMarked.length === 0 ? (
              <div style={styles.emptyState}>
                <p style={styles.emptyIcon}>📋</p>
                <p style={styles.emptyText}>No attendance marked yet today</p>
              </div>
            ) : (
              recentlyMarked.map((m, i) => (
                <div key={i} style={styles.recentItem}>
                  <div style={{
                    ...styles.recentAvatar,
                    background: m.status === 'present' ? '#2ecc71' :
                      m.status === 'absent' ? '#e74c3c' : '#f39c12'
                  }}>
                    {m.first_name?.charAt(0)}{m.last_name?.charAt(0)}
                  </div>
                  <div style={styles.recentInfo}>
                    <p style={styles.recentName}>
                      {m.first_name} {m.last_name}
                    </p>
                    <p style={styles.recentId}>{m.school_id}</p>
                  </div>
                  <div style={styles.recentRight}>
                    <span style={{
                      ...styles.recentStatus,
                      background: m.status === 'present' ? '#e0ffe0' :
                        m.status === 'absent' ? '#ffe0e0' : '#fff3e0',
                      color: m.status === 'present' ? '#006600' :
                        m.status === 'absent' ? '#cc0000' : '#cc7700',
                    }}>
                      {m.status}
                    </span>
                    <p style={styles.recentTime}>{m.time}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* REPORTS TAB */}
      {activeTab === 'reports' && (
        <div style={styles.reportContainer}>
          <div style={styles.reportFilter}>
            <label style={styles.label}>Select Date</label>
            <input
              style={styles.input}
              type="date"
              value={filterDate}
              onChange={e => { setFilterDate(e.target.value); }}
            />
          </div>

          {loading ? (
            <p style={styles.loadingText}>Loading...</p>
          ) : attendanceList.length === 0 ? (
            <div style={styles.emptyState}>
              <p style={styles.emptyIcon}>📋</p>
              <p style={styles.emptyText}>No attendance records for this date</p>
            </div>
          ) : (
            <div style={styles.reportTable}>
              <div style={styles.tableHeader}>
                <span style={styles.tableCol}>Member</span>
                <span style={styles.tableCol}>School ID</span>
                <span style={styles.tableCol}>Status</span>
                <span style={styles.tableCol}>Method</span>
                <span style={styles.tableCol}>Marked By</span>
                <span style={styles.tableCol}>Time</span>
              </div>
              {attendanceList.map(a => (
                <div key={a.id} style={styles.tableRow}>
                  <span style={styles.tableCol}>
                    {a.first_name} {a.last_name}
                  </span>
                  <span style={styles.tableCol}>{a.school_id}</span>
                  <span style={styles.tableCol}>
                    <span style={{
                      ...styles.statusPill,
                      background: a.status === 'present' ? '#e0ffe0' :
                        a.status === 'absent' ? '#ffe0e0' : '#fff3e0',
                      color: a.status === 'present' ? '#006600' :
                        a.status === 'absent' ? '#cc0000' : '#cc7700',
                    }}>
                      {a.status}
                    </span>
                  </span>
                  <span style={styles.tableCol}>{a.method?.replace('_', ' ')}</span>
                  <span style={styles.tableCol}>
                    {a.marked_by_fname} {a.marked_by_lname}
                  </span>
                  <span style={styles.tableCol}>
                    {new Date(a.created_at).toLocaleTimeString('en-PH')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* SUMMARY TAB */}
      {activeTab === 'summary' && (
        <div style={styles.reportContainer}>
          {loading ? (
            <p style={styles.loadingText}>Loading...</p>
          ) : summary.length === 0 ? (
            <div style={styles.emptyState}>
              <p style={styles.emptyIcon}>📈</p>
              <p style={styles.emptyText}>No attendance data yet</p>
            </div>
          ) : (
            <div style={styles.reportTable}>
              <div style={styles.tableHeader}>
                <span style={styles.tableCol}>Member</span>
                <span style={styles.tableCol}>School ID</span>
                <span style={{ ...styles.tableCol, color: '#2ecc71' }}>Present</span>
                <span style={{ ...styles.tableCol, color: '#e74c3c' }}>Absent</span>
                <span style={{ ...styles.tableCol, color: '#f39c12' }}>Late</span>
                <span style={styles.tableCol}>Total</span>
              </div>
              {summary.map(s => (
                <div key={s.id} style={styles.tableRow}>
                  <span style={styles.tableCol}>
                    {s.first_name} {s.last_name}
                  </span>
                  <span style={styles.tableCol}>{s.school_id}</span>
                  <span style={{ ...styles.tableCol, color: '#2ecc71', fontWeight: 'bold' }}>
                    {s.present_count}
                  </span>
                  <span style={{ ...styles.tableCol, color: '#e74c3c', fontWeight: 'bold' }}>
                    {s.absent_count}
                  </span>
                  <span style={{ ...styles.tableCol, color: '#f39c12', fontWeight: 'bold' }}>
                    {s.late_count}
                  </span>
                  <span style={styles.tableCol}>{s.total}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: { padding: '0' },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  title: { margin: '0 0 5px 0', fontSize: '22px', color: '#333' },
  subtitle: { margin: 0, color: '#888', fontSize: '13px' },
  tabs: {
    display: 'flex',
    gap: '8px',
    marginBottom: '20px',
    flexWrap: 'wrap',
  },
  tab: {
    padding: '8px 18px',
    background: 'white',
    border: '1.5px solid #ddd',
    borderRadius: '20px',
    cursor: 'pointer',
    fontSize: '13px',
    color: '#666',
  },
  tabActive: {
    padding: '8px 18px',
    background: '#0f3460',
    border: '1.5px solid #0f3460',
    borderRadius: '20px',
    cursor: 'pointer',
    fontSize: '13px',
    color: 'white',
    fontWeight: 'bold',
  },
  successMsg: {
    background: '#e0ffe0', color: '#006600',
    padding: '12px', borderRadius: '8px',
    marginBottom: '15px', fontSize: '14px',
  },
  errorMsg: {
    background: '#ffe0e0', color: '#cc0000',
    padding: '12px', borderRadius: '8px',
    marginBottom: '15px', fontSize: '14px',
  },
  markContainer: {
    display: 'flex',
    gap: '20px',
    flexWrap: 'wrap',
  },
  markCard: {
    flex: '1',
    minWidth: '300px',
    background: 'white',
    borderRadius: '16px',
    padding: '25px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
  },
  recentCard: {
    width: '320px',
    background: 'white',
    borderRadius: '16px',
    padding: '25px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
    height: 'fit-content',
  },
  cardTitle: {
    margin: '0 0 20px 0',
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#333',
  },
  field: { marginBottom: '18px' },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '600',
    color: '#444',
    marginBottom: '8px',
  },
  input: {
    width: '100%',
    padding: '11px 14px',
    borderRadius: '10px',
    border: '1.5px solid #ddd',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  methodRow: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  methodBtn: {
    flex: 1,
    padding: '10px',
    background: 'white',
    border: '1.5px solid #ddd',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '13px',
    color: '#666',
  },
  methodActive: {
    flex: 1,
    padding: '10px',
    background: '#0f3460',
    border: '1.5px solid #0f3460',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '13px',
    color: 'white',
    fontWeight: 'bold',
  },
  qrBox: {
    background: '#f8f9fa',
    borderRadius: '10px',
    padding: '20px',
    textAlign: 'center',
  },
  qrText: {
    fontSize: '40px',
    margin: '0 0 5px 0',
  },
  qrSub: {
    color: '#888',
    fontSize: '13px',
    margin: '0 0 15px 0',
  },
  statusRow: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  statusBtn: {
    flex: 1,
    padding: '10px',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
    transition: 'all 0.2s',
  },
  markBtn: {
    width: '100%',
    padding: '14px',
    background: 'linear-gradient(135deg, #0f3460, #16213e)',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '15px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  markBtnDisabled: {
    width: '100%',
    padding: '14px',
    background: '#ccc',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '15px',
    fontWeight: 'bold',
    cursor: 'not-allowed',
  },
  multipleBox: {
    marginTop: '15px',
    background: '#fff3e0',
    borderRadius: '10px',
    padding: '15px',
  },
  multipleTitle: {
    margin: '0 0 10px 0',
    fontWeight: 'bold',
    color: '#333',
    fontSize: '13px',
  },
  memberSelectBtn: {
    width: '100%',
    padding: '12px',
    background: 'white',
    border: '1.5px solid #ddd',
    borderRadius: '8px',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  memberSelectName: {
    fontWeight: 'bold',
    color: '#333',
    fontSize: '14px',
  },
  memberSelectId: {
    color: '#888',
    fontSize: '12px',
  },
  emptyState: { textAlign: 'center', padding: '30px 0' },
  emptyIcon: { fontSize: '40px', margin: '0 0 10px 0' },
  emptyText: { color: '#888', fontSize: '14px', margin: 0 },
  recentItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 0',
    borderBottom: '1px solid #f5f5f5',
  },
  recentAvatar: {
    width: '38px',
    height: '38px',
    borderRadius: '50%',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    fontSize: '13px',
    flexShrink: 0,
  },
  recentInfo: { flex: 1 },
  recentName: {
    margin: '0 0 2px 0',
    fontWeight: 'bold',
    color: '#333',
    fontSize: '13px',
  },
  recentId: { margin: 0, color: '#aaa', fontSize: '11px' },
  recentRight: { textAlign: 'right' },
  recentStatus: {
    padding: '3px 8px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: 'bold',
    textTransform: 'capitalize',
  },
  recentTime: { margin: '3px 0 0 0', color: '#aaa', fontSize: '11px' },
  reportContainer: { background: 'white', borderRadius: '16px', padding: '25px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' },
  reportFilter: { marginBottom: '20px', maxWidth: '250px' },
  loadingText: { textAlign: 'center', color: '#888', padding: '30px' },
  reportTable: { overflowX: 'auto' },
  tableHeader: {
    display: 'flex',
    padding: '12px 15px',
    background: '#f8f9fa',
    borderRadius: '8px',
    marginBottom: '5px',
    fontWeight: 'bold',
    fontSize: '12px',
    color: '#666',
    textTransform: 'uppercase',
  },
  tableRow: {
    display: 'flex',
    padding: '12px 15px',
    borderBottom: '1px solid #f5f5f5',
    fontSize: '13px',
    color: '#333',
    alignItems: 'center',
  },
  tableCol: {
    flex: 1,
    minWidth: '100px',
  },
  statusPill: {
    padding: '3px 10px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: 'bold',
    textTransform: 'capitalize',
  },
};

export default Attendance;